import React, {useMemo} from 'react'
import {cn} from '@/lib/utils'
import {Button} from '@/components/ui/button'
import {BodyCopy} from '@/components/ui/body-copy'
interface InjectedContentItem {
    index: number
    content: React.ReactNode // raw HTML or JS
}

interface PaginatedGridDisplayProps {
    attributes: {
        className: string
    }
    displayedItems: any[]
    displayedItemsText?: string
    showNextEntries?: () => void
    showAllEntries?: () => void
    showAll?: boolean
    allEntriesDisplayed?: boolean
    injectedContentItems?: InjectedContentItem[]
}

export const PaginatedGridDisplay: React.FC<PaginatedGridDisplayProps> = ({
    attributes,
    displayedItems,
    displayedItemsText,
    showNextEntries,
    showAllEntries,
    showAll,
    allEntriesDisplayed,
    injectedContentItems = []
}) => {
    const {className, ...restAttributes} = attributes || {}

    // Combine displayedItems with injected content
    const combinedItems = useMemo(() => {
        const items = [...displayedItems]
        const inserted = new Set<number>()

        injectedContentItems.forEach((item) => {
            const {index, content} = item || {}

            if (inserted.has(index)) return
            inserted.add(index)

            // Clamp the index to avoid out-of-bounds errors
            const clampedIndex = Math.min(Math.max(index, 0), items.length)
            items.splice(clampedIndex, 0, content)
        })

        return items
    }, [displayedItems, injectedContentItems])

    return (
        <div className={cn('flex flex-col mx-auto', className)}>
            <div
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-1 md:gap-6 auto-rows-auto"
                style={{gridAutoFlow: 'dense'}}
                {...restAttributes}
            >
                {combinedItems.map((item, idx) => {
                    // If item is a valid React element and has a key, preserve it
                    if (React.isValidElement(item)) {
                        return React.cloneElement(item, {
                            key: item.key ?? `combined-item-${idx}`
                        })
                    }
                    // Otherwise, render as is with a wrapper
                    return <React.Fragment key={`combined-item-${idx}`}>{item}</React.Fragment>
                })}
            </div>
            {displayedItemsText && (
                <div className="mx-auto text-black mb-8 mt-[100px]">
                    <BodyCopy
                        size="small"
                        className="inline !font-medium"
                        data-testid="paginatedGridDisplay-BodyCopy"
                    >
                        {displayedItemsText}
                    </BodyCopy>
                </div>
            )}
            {showNextEntries && !showAll && !allEntriesDisplayed && (
                <Button
                    ctaType="button"
                    variant="primary"
                    onClick={showNextEntries}
                    className="w-[12.5rem] mx-auto"
                    data-testid="paginatedGridDisplay-Button"
                >
                    Load More
                </Button>
            )}
            {showAllEntries && !showAll && !allEntriesDisplayed && (
                <Button
                    ctaType="link"
                    variant="bold"
                    onClick={showAllEntries}
                    className="w-[12.5rem] mx-auto mt-8"
                    data-testid="paginatedGridDisplay-Button"
                >
                    View All
                </Button>
            )}
        </div>
    )
}
